import React from 'react';

type ToggleProps = {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    className?: string;
    activeIcon?: React.ReactNode;
    inactiveIcon?: React.ReactNode;
    variant?: 'blue' | 'green';
};

export default function Toggle({ checked, onChange, disabled = false, className = '', activeIcon, inactiveIcon, variant = 'blue' }: ToggleProps) {
    const getActiveColor = () => {
        switch (variant) {
            case 'green':
                return '#10b981'; // emerald-500
            case 'blue':
            default:
                return '#3b82f6'; // blue-500
        }
    };

    const getInactiveColor = () => {
        switch (variant) {
            case 'green':
                return '#f59e0b'; // amber-500
            case 'blue':
            default:
                return '#cbd5e1'; // slate-300
        }
    };

    const getActiveShadow = () => {
        switch (variant) {
            case 'green':
                return '0 0 0 2px rgba(16, 185, 129, 0.2)';
            case 'blue':
            default:
                return '0 0 0 2px rgba(59, 130, 246, 0.2)';
        }
    };

    const getInactiveShadow = () => {
        switch (variant) {
            case 'green':
                return '0 0 0 2px rgba(245, 158, 11, 0.2)';
            case 'blue':
            default:
                return '0 0 0 2px rgba(203, 213, 225, 0.2)';
        }
    };

    return (
        <label
            className={`toggle-container ${className}`}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1
            }}
        >
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                disabled={disabled}
                style={{ display: 'none' }}
            />
            <div
                className={`toggle-track ${checked ? 'toggle-track-active' : 'toggle-track-inactive'}`}
                style={{
                    width: '44px',
                    height: '24px',
                    borderRadius: '12px',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    backgroundColor: checked ? getActiveColor() : getInactiveColor(),
                    boxShadow: checked ? getActiveShadow() : getInactiveShadow()
                }}
            >
                <div
                    className={`toggle-thumb ${checked ? 'toggle-thumb-active' : 'toggle-thumb-inactive'}`}
                    style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        position: 'absolute',
                        top: '2px',
                        left: checked ? '22px' : '2px',
                        transition: 'left 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                        transform: 'translateZ(0)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                    }}
                >
                    {checked ? activeIcon : inactiveIcon}
                </div>
            </div>
        </label>
    );
}
